import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowLeft, Plus, Edit2, Trash2, Share2, Repeat2, Upload, Image, Film, ChevronDown, Info, Download, Wand2, BookmarkPlus, Sparkles, Trophy, Crown } from 'lucide-react';
import { UserBadgeGroup } from './UserBadge';
import EmoteOfTheWeek from './EmoteOfTheWeek';
import IdeasVotePanel from './IdeasVotePanel';
import BuildListPanel from './BuildListPanel';
import NeedsIdRail from './NeedsIdRail';
import { WallUserCard } from './WallUserCard';
import IdLeaderboardRail from './IdLeaderboardRail';
import WallSpotlightMobile from './WallSpotlightMobile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPES = [
  { value: 'music', label: 'Music', color: '#06b6d4' },
  { value: 'event_clip', label: 'Event Clip', color: '#c026d3' },
  { value: 'gig_post', label: 'Gig Post', color: '#f59e0b' },
  { value: 'calendar_event', label: 'Calendar Event', color: '#8b5cf6' },
  { value: 'jam_session_invite', label: 'Jam Session Invite', color: '#00ff88' },
  { value: 'crate_share', label: 'Crate Share', color: '#f472b6' },
  { value: 'shared_track', label: 'Shared Track', color: '#22d3ee' },
  { value: 'shared_event', label: 'Shared Event', color: '#f59e0b' },
  { value: 'general', label: 'General', color: '#94a3b8' },
];

const REACTIONS = [
  { type: 'thumbsup', emoji: '\uD83D\uDC4D' },
  { type: 'heart', emoji: '\u2764\uFE0F' },
  { type: 'smile', emoji: '\uD83D\uDE0A' },
  { type: 'fire', emoji: '\uD83D\uDD25' },
  { type: 'raised_hands', emoji: '\uD83D\uDE4C' },
  { type: 'muscle', emoji: '\uD83D\uDCAA' },
];

// System / auto-generated post types that aren't user-selectable but need their own tag
const TYPE_LABELS = {
  event_planner: { label: 'Event', color: '#f59e0b' },
  id_hunters: { label: 'ID Hunters', color: '#ff2d78' },
  creator_of_week: { label: 'Creator of the Week', color: '#fbbf24' },
  featured_emote: { label: 'Featured Emote', color: '#a78bfa' },
  poll_result: { label: 'Community Vote', color: '#fbbf24' },
  build_shipped: { label: 'Shipped', color: '#34d399' },
};
const getContentColor = (type) => TYPE_LABELS[type]?.color || CONTENT_TYPES.find(c => c.value === type)?.color || '#94a3b8';
const getContentLabel = (type) => TYPE_LABELS[type]?.label || CONTENT_TYPES.find(c => c.value === type)?.label || 'General';

// Normalise an avatar URL stored on a post / user → a fully-qualified, backend-served URL
// Handles: absolute http(s), already-prefixed /api/uploads/..., and legacy /uploads/... paths
const resolveAvatarUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('data:')) return raw;
  const normalised = raw.startsWith('/uploads/') ? raw.replace('/uploads/', '/api/uploads/') : raw;
  return `${BACKEND_URL}${normalised.startsWith('/') ? '' : '/'}${normalised}`;
};

// Small avatar component — renders the image when present and falls back to the user's initial
// circle on missing URL or load failure, so a broken thumbnail never bleeds onto the wall.
const Avatar = ({ src, name, size = 36, fontSize = 14 }) => {
  const [failed, setFailed] = useState(false);
  const resolved = resolveAvatarUrl(src);
  const showImage = !!resolved && !failed;
  return (
    <div
      style={{
        width: size, height: size, borderRadius: '50%',
        background: 'linear-gradient(135deg, #06b6d4, #c026d3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize, fontWeight: 700, flexShrink: 0, overflow: 'hidden',
      }}
    >
      {showImage ? (
        <img
          src={resolved}
          alt={name || ''}
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        (name?.charAt(0) || '?').toUpperCase()
      )}
    </div>
  );
};

// Map a post.highlight value → CSS class for the animated gradient border
const getHighlightClass = (highlight) => {
  if (!highlight || highlight === 'none') return '';
  if (highlight === 'admin') return 'wall-highlight wall-highlight-admin';
  if (highlight === 'yellow') return 'wall-highlight wall-highlight-yellow';
  if (highlight === 'red') return 'wall-highlight wall-highlight-red';
  if (highlight === 'purple') return 'wall-highlight wall-highlight-purple';
  return '';
};

// ============ SHARED TRACK CARD ============
// Renders a track card embedded inside a Wall post (from ID, Discovery, or Similar Tracks share)
const SHARED_SOURCE_BADGES = {
  id: { label: '🎯 IDed on BEATSEEK', color: '#00ff88', border: 'rgba(0,255,136,0.4)' },
  discovery: { label: '🔍 Discovered on BEATSEEK', color: '#22d3ee', border: 'rgba(6,182,212,0.4)' },
  similar: { label: '🤖 AI Match on BEATSEEK', color: '#e879f9', border: 'rgba(192,38,211,0.4)' },
  edit_index: { label: '🧬 Community Edit Index', color: '#f7d55c', border: 'rgba(247,213,92,0.45)' },
};

const VERSION_TAG = { remix: 'REMIX', edit: 'EDIT', bootleg: 'BOOTLEG', vip: 'VIP', rework: 'REWORK', flip: 'FLIP', mashup: 'MASHUP', extended: 'EXTENDED MIX', radio: 'RADIO EDIT', club: 'CLUB MIX', dub: 'DUB' };

// ── Wall ID Leaderboard (this month's top clip identifiers) + profile badge for the top 3 ──
let _leaderCache = { at: 0, data: null, promise: null };
const fetchLeaderboard = () => {
  if (_leaderCache.data && Date.now() - _leaderCache.at < 60000) return Promise.resolve(_leaderCache.data);
  if (!_leaderCache.promise) {
    _leaderCache.promise = fetch(`${BACKEND_URL}/api/wall/id-leaderboard`).then(r => r.json()).then(d => { _leaderCache = { at: Date.now(), data: d, promise: null }; return d; }).catch(() => { _leaderCache.promise = null; return null; });
  }
  return _leaderCache.promise;
};
const BADGE_STYLE = { 1: { label: '🥇 Top ID Hunter', color: '#f7d55c' }, 2: { label: '🥈 ID Hunter', color: '#d0d0d0' }, 3: { label: '🥉 ID Hunter', color: '#e0a36a' } };

const IdHunterBadge = ({ userId }) => {
  const [rank, setRank] = useState(null);
  const [streak, setStreak] = useState(0);
  useEffect(() => { let on = true; fetchLeaderboard().then(d => { if (on && d?.badges) { setRank(d.badges[userId] || null); setStreak(d.streaks?.[userId] || 0); } }); return () => { on = false; }; }, [userId]);
  if (!rank || !BADGE_STYLE[rank]) return null;
  const b = BADGE_STYLE[rank];
  return (
    <span data-testid={`id-hunter-badge-${userId}`} title={`#${rank} on this month's Wall ID leaderboard${streak >= 2 ? ` · ${streak} months running in the top 3` : ''}`} style={{ fontSize: '9px', fontWeight: 700, color: b.color, border: `1px solid ${b.color}55`, borderRadius: 999, padding: '1px 6px', marginLeft: 4, whiteSpace: 'nowrap' }}>
      {b.label}{streak >= 2 && <span data-testid={`id-hunter-streak-badge-${userId}`} style={{ color: '#ff9a3c', marginLeft: 4 }}>🔥{streak}</span>}
    </span>
  );
};
const StreakFlame = ({ streak, size = 10 }) => (streak >= 2 ? <span title={`${streak} months in a row in the top 3`} style={{ color: '#ff9a3c', fontWeight: 800, fontSize: size }}>🔥{streak}</span> : null);

const WallIdLeaderboard = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { fetchLeaderboard().then(setData); }, []);
  if (!data || !data.leaders?.length) return null;
  const top = data.leaders[0];
  return (
    <div data-testid="wall-id-leaderboard" style={{ marginBottom: '10px', borderRadius: '10px', border: '1px solid rgba(0,229,255,0.25)', background: 'rgba(0,229,255,0.05)', fontSize: '12px' }}>
      <button type="button" onClick={() => setOpen(!open)} data-testid="wall-id-leaderboard-toggle" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'none', border: 0, color: '#fff', cursor: 'pointer', textAlign: 'left' }}>
        <span>🎯</span>
        <span style={{ fontWeight: 700 }}>Wall ID Leaderboard · {data.month}</span>
        <span style={{ color: 'rgba(255,255,255,0.6)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>🥇 {top.username} · {top.ids} clip{top.ids !== 1 ? 's' : ''} ID'd</span>
        <span style={{ color: 'rgba(255,255,255,0.4)' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <ol style={{ margin: 0, padding: '0 12px 10px 12px', listStyle: 'none' }}>
          {data.leaders.map(l => (
            <li key={l.user_id} data-testid="wall-id-leader-row" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ width: 22, color: BADGE_STYLE[l.rank]?.color || 'rgba(255,255,255,0.5)', fontWeight: 700 }}>#{l.rank}</span>
              {l.avatar ? <img src={l.avatar} alt="" style={{ width: 20, height: 20, borderRadius: '50%' }} /> : <span style={{ width: 20 }} />}
              <span style={{ flex: 1, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 }}>{l.username}<StreakFlame streak={l.streak} /></span>
              <span style={{ color: '#00e5ff', fontWeight: 700 }}>{l.ids}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

// ── Request This Edit ("I want this") on Edit Index cards ──
const WantThisButton = ({ editId, externalUrl }) => {
  const [state, setState] = useState({ count: 0, requested: false, link: externalUrl || null });
  useEffect(() => {
    if (!editId) return;
    const token = localStorage.getItem('beatseek_token');
    fetch(`${BACKEND_URL}/api/community-index/${editId}/requests`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (d) setState({ count: d.request_count || 0, requested: !!d.requested, link: d.link || externalUrl || null }); }).catch(() => {});
  }, [editId, externalUrl]);
  if (!editId) return null;
  const toggle = async (e) => {
    e.stopPropagation(); e.preventDefault();
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;
    const r = await fetch(`${BACKEND_URL}/api/community-index/${editId}/request`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { const d = await r.json(); setState(s => ({ ...s, count: d.request_count, requested: d.requested })); }
  };
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }} onClick={e => e.stopPropagation()}>
      <button type="button" onClick={toggle} data-testid={`want-this-btn-${editId}`} style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${state.requested ? '#ff2d78' : 'rgba(255,45,120,0.5)'}`, background: state.requested ? 'rgba(255,45,120,0.25)' : 'rgba(255,45,120,0.08)', color: '#ff7aa8' }}>
        🔥 {state.requested ? 'You want this' : 'I want this'}{state.count > 0 ? ` · ${state.count}` : ''}
      </button>
      {state.link && <a href={state.link} target="_blank" rel="noopener noreferrer" data-testid={`get-edit-link-${editId}`} style={{ fontSize: 11, fontWeight: 700, color: '#f7d55c', textDecoration: 'none' }}>Get it ↗</a>}
    </div>
  );
};

// Auto-ID: run a Wall video clip through the ID engine and tag the post
const WallTrackIdTag = ({ post, currentUserId }) => {
  const [tag, setTag] = useState(post.identified_track || null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  useEffect(() => { setTag(post.identified_track || null); }, [post.identified_track]);
  if (post.media_type !== 'video') return null;

  const run = async (e) => {
    e.stopPropagation();
    if (!currentUserId) { setNote('Log in to ID clips'); return; }
    setBusy(true); setNote(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/wall/posts/${post.id}/identify`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('beatseek_token')}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'ID failed');
      if (d.status === 'match' || d.status === 'possible') setTag(d.identified_track);
      else setNote(d.status === 'not_electronic' ? `Heard ${d.identified_track?.artist} – ${d.identified_track?.title}, but it isn't electronic` : d.reason === 'too_quiet' ? 'Clip audio is too quiet to ID' : 'No match — not in any database (yet)');
    } catch (err) {
      setNote(err.message);
    } finally { setBusy(false); }
  };

  if (tag && tag.title) {
    return (
      <div data-testid={`wall-id-tag-${post.id}`} onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', marginBottom: '10px', borderRadius: '10px', background: 'rgba(0,229,255,0.07)', border: '1px solid rgba(0,229,255,0.35)', fontSize: '12px' }}>
        {tag.artwork ? <img src={tag.artwork} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover' }} /> : <span style={{ fontSize: 18 }}>🎯</span>}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tag.artist} – {tag.title}</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px' }}>
            {VERSION_TAG[tag.version_type] && <span style={{ color: '#ff2d78', fontWeight: 700, marginRight: 6 }}>{VERSION_TAG[tag.version_type]}{tag.remixer ? ` · ${tag.remixer}` : ''}</span>}
            {tag.canonical_genre && <span style={{ marginRight: 6 }}>{tag.canonical_genre}</span>}
            {tag.community && <span style={{ color: '#f7d55c', marginRight: 6 }}>🧬 community edit</span>}
            {tag.status === 'possible' ? 'possible match' : `${Math.round(tag.confidence || 0)}%`} · ID'd by {tag.identified_by || 'BEATSEEK'}
          </div>
        </div>
        <button type="button" onClick={run} disabled={busy} title="Re-scan" data-testid={`wall-id-rescan-${post.id}`} style={{ background: 'none', border: 0, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 12 }}>{busy ? '…' : '↻'}</button>
      </div>
    );
  }
  return (
    <div onClick={e => e.stopPropagation()} style={{ marginBottom: '10px' }}>
      <button type="button" onClick={run} disabled={busy} data-testid={`wall-id-btn-${post.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, border: '1px solid rgba(0,229,255,0.5)', background: 'rgba(0,229,255,0.1)', color: '#00e5ff', fontSize: 12, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
        🎯 {busy ? 'Listening to the clip…' : 'ID this track'}
      </button>
      {note && <span data-testid={`wall-id-note-${post.id}`} style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{note}</span>}
    </div>
  );
};

const SharedTrackCard = ({ track, compact = false }) => {
  if (!track) return null;
  const badge = SHARED_SOURCE_BADGES[track.source] || SHARED_SOURCE_BADGES.discovery;
  const size = compact ? 56 : 72;
  const hasSpotify = !!track.spotify_uri;
  return (
    <div
      data-testid="wall-shared-track"
      style={{
        display: 'flex', gap: compact ? '10px' : '12px',
        padding: compact ? '8px' : '10px',
        background: 'linear-gradient(135deg, rgba(6,182,212,0.06), rgba(192,38,211,0.06))',
        border: `1px solid ${badge.border}`,
        borderRadius: '10px', marginBottom: '10px',
      }}
    >
      <div
        style={{
          width: size, height: size, borderRadius: '8px', flexShrink: 0,
          background: track.artwork
            ? `url(${track.artwork}) center/cover no-repeat`
            : 'linear-gradient(135deg, #1a1a2e, #0d0d14)',
          border: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#22d3ee', fontSize: '9px', fontWeight: 700,
        }}
      >
        {!track.artwork && 'BEATSEEK'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: compact ? '12px' : '13px', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {track.title || 'Untitled'}
        </div>
        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {track.artist || 'Unknown'}
        </div>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
          {track.bpm && (
            <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(255,170,0,0.08)', color: '#fbbf24', fontWeight: 600 }}>
              🥁 {track.bpm}
            </span>
          )}
          {track.key && (
            <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(192,38,211,0.10)', color: '#e879f9', fontWeight: 600 }}>
              🎹 {track.key}
            </span>
          )}
          {track.genre && (
            <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(6,182,212,0.08)', color: '#22d3ee', fontWeight: 600 }}>
              {track.genre}
            </span>
          )}
          {VERSION_TAG[track.version_type] && (
            <span data-testid="wall-shared-track-version" style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(255,45,120,0.1)', color: '#ff2d78', fontWeight: 700 }}>
              {VERSION_TAG[track.version_type]}{track.original_artist ? ` · of ${track.original_artist}${track.original_title ? ` – ${track.original_title}` : ''}` : ''}
            </span>
          )}
        </div>
        {track.source === 'edit_index' && track.edit_id && <WantThisButton editId={track.edit_id} externalUrl={track.external_url} />}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px', gap: '6px' }}>
          <span
            style={{
              fontSize: '9px', padding: '2px 7px', borderRadius: '8px',
              color: badge.color, border: `1px solid ${badge.border}`,
              fontWeight: 700, letterSpacing: '0.02em',
              background: 'rgba(0,0,0,0.25)',
            }}
          >
            {badge.label}
          </span>
          {hasSpotify && (
            <a
              href={track.spotify_uri.startsWith('spotify:') ? `https://open.spotify.com/track/${track.spotify_uri.split(':').pop()}` : track.spotify_uri}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                fontSize: '9px', padding: '3px 8px', borderRadius: '8px',
                background: 'rgba(30,215,96,0.12)', color: '#1ed760',
                border: '1px solid rgba(30,215,96,0.4)', fontWeight: 700,
                textDecoration: 'none', flexShrink: 0,
              }}
              data-testid="wall-shared-track-spotify"
            >
              ▶ Spotify
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ SHARED EVENT CARD ============
// Renders an event/gig/jam card embedded inside a Wall post
const SHARED_EVENT_BADGES = {
  calendar_event: { label: '🗓️ Event on BEATSEEK', color: '#a78bfa', border: 'rgba(139,92,246,0.4)', icon: '🗓️' },
  gig: { label: '💼 Gig on BEATSEEK', color: '#fbbf24', border: 'rgba(245,158,11,0.4)', icon: '💼' },
  jam: { label: '🎧 Live Jam Session', color: '#00ff88', border: 'rgba(0,255,136,0.4)', icon: '🎧' },
};

const SharedEventCard = ({ event, compact = false }) => {
  if (!event) return null;
  const badge = SHARED_EVENT_BADGES[event.source] || SHARED_EVENT_BADGES.calendar_event;
  const size = compact ? 56 : 72;
  const chips = [event.date_line, event.location && `📍 ${event.location}`, event.genre && `🎵 ${event.genre}`, event.extra].filter(Boolean);
  return (
    <div
      data-testid="wall-shared-event"
      style={{
        display: 'flex', gap: compact ? '10px' : '12px',
        padding: compact ? '8px' : '10px',
        background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(245,158,11,0.05))',
        border: `1px solid ${badge.border}`,
        borderRadius: '10px', marginBottom: '10px',
      }}
    >
      <div
        style={{
          width: size, height: size, borderRadius: '8px', flexShrink: 0,
          background: event.image
            ? `url(${event.image}) center/cover no-repeat`
            : 'linear-gradient(135deg, #1a1a2e, #0d0d14)',
          border: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: compact ? '22px' : '28px',
        }}
      >
        {!event.image && badge.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: compact ? '12px' : '13px', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {event.title || 'Untitled'}
        </div>
        {event.subtitle && (
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {event.subtitle}
          </div>
        )}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
          {chips.map((c, i) => (
            <span key={i} style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(6,182,212,0.08)', color: '#22d3ee', fontWeight: 600 }}>
              {c}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px', gap: '6px' }}>
          <span
            style={{
              fontSize: '9px', padding: '2px 7px', borderRadius: '8px',
              color: badge.color, border: `1px solid ${badge.border}`,
              fontWeight: 700, letterSpacing: '0.02em',
              background: 'rgba(0,0,0,0.25)',
            }}
          >
            {badge.label}
          </span>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            {event.session_id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.dispatchEvent(new CustomEvent('beatseek:join-jam-session', { detail: { sessionId: event.session_id } }));
                }}
                style={{
                  fontSize: '9px', padding: '3px 8px', borderRadius: '8px',
                  background: 'rgba(0,255,136,0.12)', color: '#00ff88',
                  border: '1px solid rgba(0,255,136,0.4)', fontWeight: 700, cursor: 'pointer',
                }}
                data-testid="wall-shared-event-join-jam"
              >
                ▶ Join Session
              </button>
            )}
            {event.link && !event.session_id && (
              <a
                href={event.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  fontSize: '9px', padding: '3px 8px', borderRadius: '8px',
                  background: 'rgba(6,182,212,0.12)', color: '#22d3ee',
                  border: '1px solid rgba(6,182,212,0.4)', fontWeight: 700,
                  textDecoration: 'none',
                }}
                data-testid="wall-shared-event-link"
              >
                🔗 Details
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// CTA on auto-generated event announcements — records a click-through then opens the planner event
const AnnouncementClaimCTA = ({ eventId, compact = false }) => {
  if (!eventId) return null;
  const go = (e) => {
    e.stopPropagation();
    try {
      fetch(`${BACKEND_URL}/api/planner/events/${eventId}/track/announcement-click`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('beatseek_token')}` },
      }).catch(() => {});
    } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent('openEventPlanner', { detail: { eventId } }));
  };
  return (
    <button
      onClick={go}
      data-testid="wall-announcement-claim-cta"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        fontSize: compact ? '11px' : '12px', fontWeight: 700,
        padding: compact ? '6px 12px' : '8px 14px', borderRadius: '10px',
        marginBottom: '10px', cursor: 'pointer',
        background: 'linear-gradient(135deg, rgba(217,70,239,0.18), rgba(147,51,234,0.18))',
        border: '1px solid rgba(217,70,239,0.5)', color: '#f0abfc',
      }}
    >
      🎛️ View event & claim a slot →
    </button>
  );
};

// ============ VIDEO HOVER PREVIEW ============
// Plays a muted 5-second loop on desktop hover and on mobile when scrolled into view.
// Falls back to static first frame when not active. Tapping the card still expands the post.
const VideoHoverPreview = ({ src, style }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [active, setActive] = useState(false);

  // Mobile: auto-trigger when the video scrolls into the viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Skip IntersectionObserver on desktop where hover handles it
    const isTouch = window.matchMedia('(hover: none)').matches;
    if (!isTouch) return;
    const obs = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting && entry.intersectionRatio >= 0.6),
      { threshold: [0, 0.6, 1] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Drive play / pause / 5-second loop cap
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      v.pause();
      try { v.currentTime = 0; } catch (_) {}
    }
  }, [active]);

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (v && v.currentTime >= 5) {
      v.currentTime = 0;
      if (v.paused && active) v.play().catch(() => {});
    }
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      style={{ position: 'relative', width: '100%' }}
    >
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        loop
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        style={style}
      />
      {!active && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            width: '46px', height: '46px', borderRadius: '50%',
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
          }}>
            <span style={{ marginLeft: '3px', borderStyle: 'solid', borderWidth: '8px 0 8px 13px', borderColor: 'transparent transparent transparent #fff' }} />
          </div>
        </div>
      )}
    </div>
  );
};

// ============ FEATURED EMOTE COMPACT CARD ============
// Renders when post.featured_emote payload is present.
// Half-height, dedicated layout: thumbnail left + meta/actions right.
const FeaturedEmotePostCard = ({ post, currentUserId, isAdmin, onReact, onDelete }) => {
  const fe = post.featured_emote || {};
  const emoteId = fe.emote_id;
  const [toast, setToast] = React.useState(null);
  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 1800); };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!emoteId) return;
    const a = document.createElement('a');
    a.href = `${BACKEND_URL}/api/emote-lab/${emoteId}/file?d=1`;
    a.download = `${(fe.name || 'emote').replace(/[^a-z0-9_-]/gi, '_')}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    flash('Downloading…');
  };
  const handleRemix = (e) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('beatseek:open-emote-lab', {
      detail: { prompt: fe.prompt || fe.name || '', style: fe.style || 'neon' }
    }));
  };
  const handleSave = async (e) => {
    e.stopPropagation();
    if (!currentUserId || !emoteId) { flash('Sign in first'); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/emote-lab/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: currentUserId, source_emote_id: emoteId }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Save failed');
      }
      flash('Saved to library ✓');
    } catch (err) { flash(err.message || 'Save failed'); }
  };

  return (
    <div
      data-testid={`wall-post-${post.id}`}
      style={{
        position: 'relative',
        padding: '10px 12px',
        borderRadius: '10px',
        cursor: 'default',
        background: 'linear-gradient(135deg, rgba(34,211,238,0.06), rgba(232,121,249,0.06))',
        border: '1px solid rgba(34,211,238,0.25)',
        boxShadow: '0 0 0 1px rgba(192,38,211,0.10) inset',
        display: 'flex', gap: '12px', alignItems: 'center',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 0 0 1px rgba(192,38,211,0.16) inset, 0 6px 16px rgba(0,0,0,0.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 0 0 1px rgba(192,38,211,0.10) inset'; }}
    >
      {/* Curator badge */}
      <div style={{
        position: 'absolute', top: '-7px', left: '10px',
        padding: '2px 8px', borderRadius: '999px',
        background: 'linear-gradient(135deg, #22d3ee, #e879f9)',
        color: '#0a0a14', fontSize: '8px', fontWeight: 900,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
      }}>
        🏆 Featured Emote
      </div>

      {/* Thumbnail */}
      <div style={{
        width: '64px', height: '64px', flexShrink: 0,
        borderRadius: '10px', overflow: 'hidden',
        background: '#0a0a14',
        border: '1px solid rgba(34,211,238,0.25)',
      }}>
        {emoteId ? (
          <img
            src={`${BACKEND_URL}/api/emote-lab/${emoteId}/file`}
            alt={fe.name || 'emote'}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : post.media_url ? (
          <img src={`${BACKEND_URL}${post.media_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : null}
      </div>

      {/* Meta + actions */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#fff',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {fe.name || 'Emote'}
          </div>
          <div style={{ fontSize: '9px', color: '#22d3ee', whiteSpace: 'nowrap', fontWeight: 700, letterSpacing: '0.06em',
                         display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <Sparkles size={10} /> Curated by BEATSEEK
          </div>
        </div>
        <div style={{ fontSize: '9px', color: '#64748b', marginTop: '2px',
                      textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          {fe.style || ''} {fe.downloads != null && <span style={{ color: '#22d3ee' }}>· ⬇ {fe.downloads}</span>}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          <button data-testid={`fe-download-${post.id}`} onClick={handleDownload} style={compactBtn('#22d3ee')}>
            <Download size={10} /> PNG
          </button>
          <button data-testid={`fe-remix-${post.id}`} onClick={handleRemix} style={compactBtn('#e879f9')}>
            <Wand2 size={10} /> Remix
          </button>
          <button data-testid={`fe-save-${post.id}`} onClick={handleSave} style={compactBtn('#fbbf24')}>
            <BookmarkPlus size={10} /> Save
          </button>

          <div style={{ flex: 1 }} />

          {/* Plain text link — opens sidebar (does NOT close it). */}
          <span
            data-testid={`fe-see-top3-${post.id}`}
            onClick={(e) => {
              e.stopPropagation();
              window.dispatchEvent(new CustomEvent('beatseek:open-featured-sidebar'));
            }}
            style={{
              fontSize: '9px',
              color: '#22d3ee',
              cursor: 'pointer',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#67e8f9'; e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#22d3ee'; e.currentTarget.style.textDecoration = 'none'; }}
          >
            View top 3 →
          </span>
        </div>
      </div>

      {toast && (
        <div style={{
          position: 'absolute', bottom: '-26px', right: '8px',
          background: '#0d0d14', border: '1px solid rgba(34,211,238,0.4)',
          borderRadius: '999px', padding: '4px 10px',
          fontSize: '9px', color: '#22d3ee', fontWeight: 700,
          whiteSpace: 'nowrap',
        }}>{toast}</div>
      )}
    </div>
  );
};

const compactBtn = (color) => ({
  padding: '4px 8px', fontSize: '9px', fontWeight: 700,
  background: 'rgba(255,255,255,0.04)',
  border: `1px solid ${color}40`,
  color, borderRadius: '6px', cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: '3px',
  letterSpacing: '0.04em', transition: 'all 0.15s',
});

// ============ CREATOR OF THE WEEK COMPACT CARD ============
// Renders when post.content_type === 'creator_of_week'.
// Celebrates the original creator behind the past 7 days' top-performing emote(s).
const CreatorOfWeekPostCard = ({ post }) => {
  const fe = post.featured_emote || {};
  const handleVisitProfile = (e) => {
    e.stopPropagation();
    if (fe.author_user_id) window.location.assign(`/profile/${fe.author_user_id}`);
  };
  return (
    <div
      data-testid={`wall-post-${post.id}`}
      style={{
        position: 'relative',
        padding: '12px 14px',
        borderRadius: '12px',
        cursor: 'default',
        background: 'linear-gradient(135deg, rgba(251,191,36,0.12), rgba(192,38,211,0.10))',
        border: '1px solid rgba(251,191,36,0.45)',
        boxShadow: '0 0 0 1px rgba(251,191,36,0.18) inset, 0 6px 22px rgba(0,0,0,0.35)',
        display: 'flex', gap: '14px', alignItems: 'center',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{
        position: 'absolute', top: '-9px', left: '12px',
        padding: '3px 10px', borderRadius: '999px',
        background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
        color: '#0a0a14', fontSize: '9px', fontWeight: 900,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        boxShadow: '0 2px 10px rgba(251,191,36,0.45)',
        display: 'inline-flex', alignItems: 'center', gap: '4px',
      }}>
        <Crown size={10} /> Creator of the Week
      </div>

      {/* Featured emote thumbnail (if any) */}
      {fe.emote_id && (
        <div style={{
          width: '70px', height: '70px', flexShrink: 0,
          borderRadius: '12px', overflow: 'hidden',
          background: '#0a0a14',
          border: '2px solid rgba(251,191,36,0.55)',
          boxShadow: '0 0 14px rgba(251,191,36,0.25)',
        }}>
          <img src={`${BACKEND_URL}/api/emote-lab/${fe.emote_id}/file`} alt={fe.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          onClick={handleVisitProfile}
          style={{ fontSize: '15px', fontWeight: 900, color: '#fff', cursor: 'pointer',
                   display: 'inline-flex', alignItems: 'center', gap: '6px',
                   whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          🏆 @{fe.author_username || 'creator'}
        </div>
        <div style={{ fontSize: '11px', color: '#fbbf24', marginTop: '3px', fontWeight: 700,
                       letterSpacing: '0.04em' }}>
          Top creator this week · {fe.emote_count || 1} new emote{(fe.emote_count || 1) === 1 ? '' : 's'} for the community
        </div>
        {post.caption && (
          <div style={{ fontSize: '11px', color: '#cbd5e1', marginTop: '6px', lineHeight: 1.4 }}>
            {post.caption}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
          <button
            data-testid={`cow-visit-${post.id}`}
            onClick={handleVisitProfile}
            style={{
              padding: '5px 12px',
              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              border: 'none', color: '#0a0a14',
              fontWeight: 800, fontSize: '10px', letterSpacing: '0.06em',
              borderRadius: '999px', cursor: 'pointer',
              textTransform: 'uppercase',
            }}
          >
            Visit Profile →
          </button>
          <span style={{ fontSize: '9px', color: '#94a3b8', letterSpacing: '0.06em' }}>
            ✨ Curated by BEATSEEK
          </span>
        </div>
      </div>
    </div>
  );
};

// ============ MONTHLY ID HUNTERS CARD ============
// Renders when post.content_type === 'id_hunters' — BEATSEEK's monthly shout-out to the top 3 Wall ID Hunters.
const HUNTER_STYLE = { 1: { icon: '🥇', color: '#f7d55c' }, 2: { icon: '🥈', color: '#d0d0d0' }, 3: { icon: '🥉', color: '#e0a36a' } };
const IdHuntersPostCard = ({ post }) => {
  const h = post.id_hunters || {};
  const leaders = h.leaders || [];
  return (
    <div
      data-testid={`wall-post-${post.id}`}
      style={{
        position: 'relative', padding: '16px 14px 12px', borderRadius: '12px',
        background: 'linear-gradient(135deg, rgba(255,45,120,0.12), rgba(0,229,255,0.08))',
        border: '1px solid rgba(255,45,120,0.4)',
        boxShadow: '0 0 0 1px rgba(255,45,120,0.15) inset, 0 6px 22px rgba(0,0,0,0.35)',
      }}
    >
      <div style={{
        position: 'absolute', top: '-9px', left: '12px', padding: '3px 10px', borderRadius: '999px',
        background: 'linear-gradient(135deg, #ff2d78, #ff7aa8)', color: '#0a0a14', fontSize: '9px', fontWeight: 900,
        letterSpacing: '0.14em', textTransform: 'uppercase', boxShadow: '0 2px 10px rgba(255,45,120,0.45)',
      }}>
        🎯 {h.month || 'Monthly'} ID Hunters
      </div>
      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
        The crew who named the most Wall clips{h.total_ids ? ` · ${h.total_ids} clips ID'd by ${h.total_hunters} hunter${h.total_hunters === 1 ? '' : 's'}` : ''}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
        {leaders.map(l => {
          const s = HUNTER_STYLE[l.rank] || HUNTER_STYLE[3];
          return (
            <div
              key={l.user_id}
              data-testid={`id-hunters-rank-${l.rank}`}
              onClick={() => window.location.assign(`/profile/${l.user_id}`)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '10px', cursor: 'pointer',
                background: `${s.color}10`, border: `1px solid ${s.color}55`,
                boxShadow: l.rank === 1 ? `0 0 14px ${s.color}33` : 'none',
              }}
            >
              <span style={{ fontSize: l.rank === 1 ? '22px' : '18px' }}>{s.icon}</span>
              {l.avatar ? (
                <img src={l.avatar.startsWith('http') ? l.avatar : `${BACKEND_URL}${l.avatar.replace('/uploads/', '/api/uploads/')}`} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a1a2a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#fff', fontWeight: 700 }}>
                  {(l.username || '?').charAt(0).toUpperCase()}
                </span>
              )}
              <span style={{ flex: 1, fontWeight: 800, color: '#fff', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>@{l.username} <StreakFlame streak={l.streak} size={11} /></span>
              <span style={{ fontSize: '11px', fontWeight: 700, color: s.color, whiteSpace: 'nowrap' }}>{l.ids} clip{l.ids === 1 ? '' : 's'}</span>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '10px', color: '#00e5ff', fontWeight: 700, letterSpacing: '0.04em' }}>
          {h.scope === 'current' ? 'Standings so far this month' : 'Fresh leaderboard is live — hit ID on any Wall clip to climb it'}
        </span>
        <span style={{ fontSize: '9px', color: '#94a3b8', letterSpacing: '0.06em' }}>✨ Curated by BEATSEEK</span>
      </div>
      {(h.on_fire || []).length > 0 && (
        <div data-testid="id-hunters-on-fire" style={{
          marginTop: '10px', padding: '8px 12px', borderRadius: '10px',
          background: 'linear-gradient(90deg, rgba(255,90,0,0.22), rgba(255,45,120,0.12))',
          border: '1px solid rgba(255,140,50,0.6)', boxShadow: '0 0 18px rgba(255,120,40,0.25)',
        }}>
          <div style={{ fontSize: '9px', fontWeight: 900, letterSpacing: '0.16em', color: '#ffb066', textTransform: 'uppercase' }}>🔥 On Fire</div>
          {h.on_fire.map(f => (
            <div key={f.user_id} data-testid={`id-hunters-on-fire-${f.user_id}`} style={{ fontSize: '12px', color: '#fff', marginTop: 3 }}>
              <b>@{f.username}</b> — <span style={{ color: '#ff9a3c', fontWeight: 800 }}>{f.streak} months straight</span> in the top 3. Untouchable.
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ POLL RESULT (COMMUNITY VOTE) CARD ============
// Renders when post.content_type === 'poll_result'. Gold animated border for
// "Build it" wins; subdued styling for pass/tie. Reactions + share enabled.
const PollResultPostCard = ({ post, currentUserId, isAdmin, onReact, onDelete, onShare }) => {
  const pr = post.featured_emote || {};
  const isShipped = post.content_type === 'build_shipped';
  const outcome = pr.outcome || 'build';
  const total = (pr.build_votes || 0) + (pr.pass_votes || 0);
  const pct = (n) => total === 0 ? 0 : Math.round((n / total) * 100);
  const buildPct = pct(pr.build_votes || 0);
  const passPct = pct(pr.pass_votes || 0);
  const isBuild = outcome === 'build';

  const ribbon = isShipped
    ? { text: "🚀 It's Live!", bg: 'linear-gradient(135deg, #34d399, #059669)', color: '#022c22' }
    : isBuild
    ? { text: '🎉 Community Chose: Build It!', bg: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: '#1a1305' }
    : outcome === 'pass'
      ? { text: '🗳️ Community Vote Result', bg: 'linear-gradient(135deg, #64748b, #475569)', color: '#fff' }
      : { text: '🗳️ Community Vote — Dead Heat', bg: 'linear-gradient(135deg, #64748b, #475569)', color: '#fff' };

  const tagline = isShipped
    ? 'You voted. We built it. Go try it! 🚀'
    : isBuild
    ? 'This idea is headed to the build list. 🚀'
    : outcome === 'pass'
      ? 'The Beatseekers passed on this one — for now.'
      : 'Too close to call — staying on the radar.';

  const cardClass = isShipped ? 'build-green-card' : (isBuild ? 'poll-gold-card' : '');

  return (
    <div className={cardClass} data-testid={`wall-post-${post.id}`}
         style={{ marginTop: '10px', ...(cardClass ? {} : { padding: '2px', borderRadius: '14px', background: 'rgba(100,116,139,0.35)' }) }}>
      <div className="poll-gold-inner" style={{ position: 'relative', padding: '16px 14px 12px' }}>
        {/* Ribbon */}
        <div style={{
          position: 'absolute', top: '-11px', left: '12px',
          padding: '3px 12px', borderRadius: '999px',
          background: ribbon.bg, color: ribbon.color,
          fontSize: '10px', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase',
          boxShadow: isShipped ? '0 2px 12px rgba(52,211,153,0.5)' : isBuild ? '0 2px 12px rgba(251,191,36,0.5)' : '0 2px 8px rgba(0,0,0,0.4)',
          whiteSpace: 'nowrap',
        }}>
          {ribbon.text}
        </div>

        {/* Author row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', marginBottom: '10px' }}>
          <Avatar src={post.avatar} name={post.username} size={28} fontSize={12} />
          <span style={{ fontSize: '12px', fontWeight: 800, color: '#fff' }}>{post.username}</span>
          <IdHunterBadge userId={post.user_id} />
          <span style={{ fontSize: '10px', color: '#555' }}>
            {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </span>
        </div>

        {/* Winning idea text */}
        <p style={{ color: '#e6e9f2', fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.5, margin: '0 0 4px' }}>
          “{pr.poll_text}”
        </p>
        {post.caption && (
          <p style={{ color: '#94a3b8', fontSize: '0.78rem', lineHeight: 1.45, margin: '0 0 12px' }}>{post.caption}</p>
        )}

        {/* Final result bars */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '10px' }} data-testid={`poll-result-bars-${post.id}`}>
          <div className="poll-bar-track">
            <div className="poll-bar-fill" style={{ width: `${buildPct}%`, background: 'linear-gradient(90deg, rgba(0,212,255,0.55), rgba(0,212,255,0.25))' }} />
            <span className="poll-bar-label" style={{ fontWeight: outcome === 'build' ? 800 : 500 }}>
              🔥 Build it {outcome === 'build' && '🏆'}
            </span>
            <span className="poll-bar-pct">{buildPct}%</span>
          </div>
          <div className="poll-bar-track">
            <div className="poll-bar-fill" style={{ width: `${passPct}%`, background: 'linear-gradient(90deg, rgba(236,72,153,0.55), rgba(236,72,153,0.25))' }} />
            <span className="poll-bar-label" style={{ fontWeight: outcome === 'pass' ? 800 : 500 }}>
              🤔 Pass {outcome === 'pass' && '🏆'}
            </span>
            <span className="poll-bar-pct">{passPct}%</span>
          </div>
          <span style={{ color: '#6b7280', fontSize: '0.68rem' }}>
            {total} Beatseeker{total === 1 ? '' : 's'} voted · {tagline}
          </span>
        </div>

        {/* Reactions + share + admin delete */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
          {REACTIONS.map(r => {
            const count = post.reaction_counts?.[r.type] || 0;
            const userReacted = (post.reactions?.[r.type] || []).includes(currentUserId);
            return (
              <button
                key={r.type}
                data-testid={`reaction-${r.type}-${post.id}`}
                onClick={() => onReact(post.id, r.type)}
                title={r.type}
                style={{
                  padding: '3px 7px', fontSize: '13px', borderRadius: '8px', cursor: 'pointer',
                  border: userReacted ? '1px solid rgba(251,191,36,0.5)' : '1px solid rgba(255,255,255,0.08)',
                  background: userReacted ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.03)',
                  color: '#ccc', display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.15s',
                }}
              >
                <span>{r.emoji}</span>
                <span style={{ fontSize: '10px', color: '#888', fontWeight: 600, minWidth: '8px', textAlign: 'left' }}>{count}</span>
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <button
            data-testid={`share-btn-${post.id}`}
            onClick={() => onShare(post)}
            style={{ padding: '4px 8px', fontSize: '10px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#888', display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <Share2 size={10} /> Share
          </button>
          {isAdmin && (
            <button
              data-testid={`delete-btn-${post.id}`}
              onClick={() => onDelete(post.id)}
              style={{ padding: '4px 6px', fontSize: '10px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(255,80,80,0.15)', background: 'rgba(255,80,80,0.05)', color: '#ff5555' }}
            >
              <Trash2 size={10} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ BOOST ANNOUNCEMENT ============
const BOOST_TYPES = ['event_planner', 'calendar_event', 'shared_event', 'gig_post', 'event_clip'];
const isBoostEligible = (post) => !!(post.planner_event_id || post.shared_event || BOOST_TYPES.includes(post.content_type));
const boostActive = (post) => !!(post.boosted_until && new Date(post.boosted_until).getTime() > Date.now());
const boostLeft = (post) => {
  const ms = new Date(post.boosted_until).getTime() - Date.now();
  if (ms <= 0) return 'ending';
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return h >= 1 ? `${h}h left` : `${m}m left`;
};

const BoostRibbon = ({ post }) => {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick(x => x + 1), 60000); return () => clearInterval(t); }, []);
  if (!boostActive(post)) return null;
  return (
    <div data-testid={`boost-ribbon-${post.id}`} style={{
      position: 'absolute', top: '-9px', right: '12px', padding: '3px 10px', borderRadius: '999px',
      background: 'linear-gradient(135deg, #f7d55c, #ffb347)', color: '#0a0a14', fontSize: '9px', fontWeight: 900,
      letterSpacing: '0.12em', textTransform: 'uppercase', boxShadow: '0 2px 12px rgba(247,213,92,0.5)', display: 'inline-flex', gap: 6, alignItems: 'center',
    }}>
      ⚡ Boosted <span style={{ fontWeight: 700, letterSpacing: 0, textTransform: 'none' }}>· {boostLeft(post)}</span>
    </div>
  );
};

const BoostModal = ({ post, onClose, onBoosted, isAdmin }) => {
  const [info, setInfo] = useState(null);
  const [state, setState] = useState({ busy: false });
  useEffect(() => { fetch(`${BACKEND_URL}/api/wall/boost/info`).then(r => r.json()).then(setInfo).catch(() => setInfo({ price_usd: 3.99, duration_hours: 24, payments_live: false })); }, []);
  const active = boostActive(post);
  const run = async () => {
    setState({ busy: true });
    try {
      const r = await fetch(`${BACKEND_URL}/api/wall/posts/${post.id}/boost`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('beatseek_token')}` } });
      const d = await r.json();
      if (r.status === 402) { setState({ comingSoon: true, msg: d.detail?.message }); return; }
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Boost failed');
      setState({ done: true, until: d.boosted_until, extended: d.extended });
      onBoosted({ ...post, boosted_until: d.boosted_until, boost_count: d.boost_count, is_boosted: true });
    } catch (e) { setState({ error: e.message }); }
  };
  const price = info?.price_usd ?? 3.99, hours = info?.duration_hours ?? 24;
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="boost-modal" onClick={e => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #14120a 0%, #0a0e1a 100%)', border: '1px solid rgba(247,213,92,0.35)', boxShadow: '0 0 40px rgba(247,213,92,0.12)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(247,213,92,0.15)' }}>
          <h3 className="text-white font-bold text-base">⚡ {active ? 'Extend your boost' : 'Boost this announcement'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="boost-modal-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm text-gray-300">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-black" style={{ color: '#f7d55c' }} data-testid="boost-price">${price.toFixed(2)}</span>
            <span className="text-gray-400">/ {hours}h at the top of the Wall</span>
          </div>
          <ul className="space-y-1.5 text-[13px]">
            <li>📌 Pinned above every post in All + Following feeds for {hours} hours</li>
            <li>✨ Gold BOOSTED ribbon with a live countdown</li>
            <li>🔁 Re-boost any time while it's live to add another {hours}h</li>
            <li>🎛️ DJs see your event first when they open the Wall</li>
          </ul>
          {active && <p className="text-[12px]" style={{ color: '#f7d55c' }}>Currently boosted · {boostLeft(post)}. Extending adds {hours}h on top.</p>}
          {state.comingSoon && (
            <div className="rounded-lg px-3 py-2 text-[12px]" style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.3)', color: '#9ee7ff' }} data-testid="boost-coming-soon">
              🚧 {state.msg || 'Boost payments open at public launch — we saved your spot and will ping you.'}
            </div>
          )}
          {state.done && <div className="rounded-lg px-3 py-2 text-[12px]" style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.35)', color: '#6ee7b7' }} data-testid="boost-done">✅ {state.extended ? 'Extended' : 'Boosted'} until {new Date(state.until).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>}
          {state.error && <p className="text-[12px] text-red-400" data-testid="boost-error">{state.error}</p>}
        </div>
        <div className="px-5 pb-5 flex flex-col gap-2">
          {!state.done && !state.comingSoon && (
            <button onClick={run} disabled={state.busy} data-testid="boost-confirm-btn" className="w-full rounded-full py-2.5 font-extrabold text-sm transition-transform hover:scale-[1.02] disabled:opacity-60" style={{ background: 'linear-gradient(135deg, #f7d55c, #ffb347)', color: '#0a0a14' }}>
              {state.busy ? 'Boosting…' : isAdmin ? `⚡ ${active ? 'Extend' : 'Boost'} now · admin preview (free)` : info && !info.payments_live ? `⚡ Boost for $${price.toFixed(2)} · Stripe coming soon` : `⚡ Boost for $${price.toFixed(2)}`}
            </button>
          )}
          {info && !info.payments_live && !state.done && !isAdmin && <p className="text-center text-[10px] text-gray-500" data-testid="boost-payments-note">Secure Stripe checkout lands with the public launch. Tapping now saves your spot.</p>}
          {(state.done || state.comingSoon) && <button onClick={onClose} className="w-full rounded-full py-2 text-sm text-gray-300 border border-white/10 hover:bg-white/5" data-testid="boost-modal-done">Done</button>}
        </div>
      </div>
    </div>,
    document.body
  );
};

const BoostButton = ({ post, currentUserId, isAdmin, onBoosted, size = 'sm' }) => {
  const [open, setOpen] = useState(false);
  if (!currentUserId || (post.user_id !== currentUserId && !isAdmin) || !isBoostEligible(post)) return null;
  const active = boostActive(post);
  const big = size === 'lg';
  return (
    <>
      <button
        data-testid={`boost-btn-${post.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={active ? 'Extend boost by 24h' : 'Boost to the top of the Wall for 24h'}
        style={{ padding: big ? '6px 12px' : '4px 8px', fontSize: big ? '11px' : '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 700,
                 border: '1px solid rgba(247,213,92,0.45)', background: active ? 'rgba(247,213,92,0.18)' : 'rgba(247,213,92,0.07)', color: '#f7d55c', display: 'flex', alignItems: 'center', gap: '4px' }}
      >
        ⚡ {active ? 'Extend' : 'Boost'}
        {!isAdmin && <span data-testid={`boost-soon-tag-${post.id}`} style={{ fontSize: '8px', fontWeight: 900, letterSpacing: '0.08em', padding: '1px 5px', borderRadius: 999, background: 'rgba(0,229,255,0.15)', color: '#00e5ff', border: '1px solid rgba(0,229,255,0.4)' }}>SOON</span>}
      </button>
      {open && <BoostModal post={post} onClose={() => setOpen(false)} onBoosted={onBoosted} isAdmin={isAdmin} />}
    </>
  );
};

// ============ POST CARD ============
const PostCard = ({ post, currentUserId, currentUsername, currentAvatar, isAdmin, onReact, onRepost, onEdit, onDelete, onExpand, onShare, onBoosted }) => {
  // Community vote result announcement + shipped celebration
  if (post.content_type === 'poll_result' || post.content_type === 'build_shipped') {
    return <PollResultPostCard post={post} currentUserId={currentUserId} isAdmin={isAdmin} onReact={onReact} onDelete={onDelete} onShare={onShare} />;
  }
  // Auto-curated creator-of-the-week post
  if (post.content_type === 'creator_of_week') {
    return <CreatorOfWeekPostCard post={post} />;
  }
  // Monthly Wall ID Hunters shout-out
  if (post.content_type === 'id_hunters') {
    return <IdHuntersPostCard post={post} />;
  }
  // Featured emote posts get a dedicated compact renderer
  if (post.content_type === 'featured_emote' || post.featured_emote) {
    return <FeaturedEmotePostCard post={post} currentUserId={currentUserId} isAdmin={isAdmin} onReact={onReact} onDelete={onDelete} />;
  }

  const contentColor = getContentColor(post.content_type);
  const highlightClass = getHighlightClass(post.highlight);
  const isHighlighted = !!highlightClass;
  const boosted = boostActive(post);

  return (
    <div
      data-testid={`wall-post-${post.id}`}
      className={highlightClass}
      onClick={() => onExpand(post)}
      style={{
        position: 'relative',
        padding: '16px', borderRadius: '12px', cursor: 'pointer',
        background: isHighlighted ? 'transparent' : boosted ? 'linear-gradient(135deg, rgba(247,213,92,0.09), rgba(255,255,255,0.03))' : 'rgba(255,255,255,0.03)',
        border: isHighlighted ? undefined : boosted ? '1px solid rgba(247,213,92,0.5)' : '1px solid rgba(255,255,255,0.06)',
        boxShadow: boosted ? '0 0 22px rgba(247,213,92,0.15)' : undefined,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)'; if (!isHighlighted && !boosted) e.currentTarget.style.borderColor = 'rgba(6,182,212,0.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; if (!isHighlighted) { e.currentTarget.style.boxShadow = boosted ? '0 0 22px rgba(247,213,92,0.15)' : 'none'; e.currentTarget.style.borderColor = boosted ? 'rgba(247,213,92,0.5)' : 'rgba(255,255,255,0.06)'; } }}
    >
      <BoostRibbon post={post} />
      {/* Repost indicator */}
      {post.is_repost && (
        <div style={{ fontSize: '10px', color: '#666', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Repeat2 size={10} /> Reposted by {post.username} · Originally by {post.original_author}
        </div>
      )}

      {/* Header: avatar + name + time + content type */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <Avatar src={post.avatar} name={post.username} size={36} fontSize={14} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>{post.username}<UserBadgeGroup badges={post.user_badges} size="sm" /><IdHunterBadge userId={post.user_id} /></div>
          <div style={{ fontSize: '10px', color: '#555' }}>{new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
        </div>
        <span style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '10px', background: `${contentColor}20`, color: contentColor, fontWeight: 600, border: `1px solid ${contentColor}40` }}>
          {getContentLabel(post.content_type)}
        </span>
      </div>

      {/* Caption */}
      {post.caption && (
        <div style={{ fontSize: '13px', color: '#ccc', lineHeight: 1.5, marginBottom: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{post.caption}</div>
      )}

      {post.planner_event_id && <AnnouncementClaimCTA eventId={post.planner_event_id} compact />}

      {/* Shared Track Card (Phase 2 share-to-Wall) */}
      {post.shared_track && (
        <SharedTrackCard track={post.shared_track} compact />
      )}
      {post.shared_event && (
        <SharedEventCard event={post.shared_event} compact />
      )}

      {/* Media */}
      {post.media_url && (
        <div style={{ borderRadius: '8px', overflow: 'hidden', marginBottom: '10px', maxHeight: '200px' }}>
          {post.media_type === 'video' ? (
            <VideoHoverPreview src={`${BACKEND_URL}${post.media_url}`} style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', display: 'block' }} />
          ) : (
            <img src={`${BACKEND_URL}${post.media_url}`} alt="" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }} />
          )}
        </div>
      )}
      {post.media_url && <WallTrackIdTag post={post} currentUserId={currentUserId} />}

      {/* Reaction Row: 6 emoji buttons with counts inline (no "React" label) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }} onClick={e => e.stopPropagation()}>
        {REACTIONS.map(r => {
          const count = post.reaction_counts?.[r.type] || 0;
          const userReacted = (post.reactions?.[r.type] || []).includes(currentUserId);
          return (
            <button
              key={r.type}
              data-testid={`reaction-${r.type}-${post.id}`}
              onClick={() => onReact(post.id, r.type)}
              title={r.type}
              style={{
                padding: '3px 7px', fontSize: '13px', borderRadius: '8px', cursor: 'pointer',
                border: userReacted ? '1px solid rgba(6,182,212,0.45)' : '1px solid rgba(255,255,255,0.08)',
                background: userReacted ? 'rgba(6,182,212,0.12)' : 'rgba(255,255,255,0.03)',
                color: '#ccc', display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.15s',
              }}
            >
              <span>{r.emoji}</span>
              <span style={{ fontSize: '10px', color: '#888', fontWeight: 600, minWidth: '8px', textAlign: 'left' }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Action bar (repost / share / edit / delete) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
        <div style={{ flex: 1 }} />

        {/* Repost (toggle — click again to undo an accidental repost) */}
        {post.user_id !== currentUserId && (() => {
          const alreadyReposted = (post.reposted_by || []).includes(currentUserId);
          return (
            <button
              data-testid={`repost-btn-${post.id}`}
              onClick={() => onRepost(post)}
              title={alreadyReposted ? 'Undo repost' : 'Repost to your Wall'}
              style={{ padding: '4px 8px', fontSize: '10px', borderRadius: '8px', cursor: 'pointer', border: alreadyReposted ? '1px solid rgba(34,211,238,0.5)' : '1px solid rgba(255,255,255,0.08)', background: alreadyReposted ? 'rgba(34,211,238,0.12)' : 'rgba(255,255,255,0.03)', color: alreadyReposted ? '#22d3ee' : '#888', display: 'flex', alignItems: 'center', gap: '3px' }}
            >
              <Repeat2 size={10} /> {post.repost_count || 0}
            </button>
          );
        })()}

        {/* Share */}
        <button
          data-testid={`share-btn-${post.id}`}
          onClick={() => onShare(post)}
          style={{ padding: '4px 8px', fontSize: '10px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#888', display: 'flex', alignItems: 'center', gap: '3px' }}
        >
          <Share2 size={10} /> Share
        </button>

        {/* Boost (event announcements, owner/admin) */}
        <BoostButton post={post} currentUserId={currentUserId} isAdmin={isAdmin} onBoosted={onBoosted} />

        {/* Edit/Delete for own posts */}
        {(post.user_id === currentUserId || isAdmin) && (
          <>
            {post.user_id === currentUserId && (
              <button
                data-testid={`edit-btn-${post.id}`}
                onClick={() => onEdit(post)}
                title="Edit caption or content type. To change media, delete and create a new post."
                style={{ padding: '4px 6px', fontSize: '10px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#888' }}
              >
                <Edit2 size={10} />
              </button>
            )}
            <button
              data-testid={`delete-btn-${post.id}`}
              onClick={() => onDelete(post.id)}
              style={{ padding: '4px 6px', fontSize: '10px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(255,80,80,0.15)', background: 'rgba(255,80,80,0.05)', color: '#ff5555' }}
            >
              <Trash2 size={10} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ============ EXPANDED POST VIEW ============
const ExpandedPost = ({ post, currentUserId, currentUsername, currentAvatar, isAdmin, onBack, onReact, onRepost, onEdit, onDelete, onShare, onBoosted }) => {
  const contentColor = getContentColor(post.content_type);
  const highlightClass = getHighlightClass(post.highlight);
  return (
    <div className={highlightClass} style={{ padding: '20px', borderRadius: '12px', background: highlightClass ? 'transparent' : undefined }}>
      {boostActive(post) && (
        <div data-testid={`boost-status-${post.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: '12px', padding: '4px 12px', borderRadius: 999, background: 'linear-gradient(135deg, #f7d55c, #ffb347)', color: '#0a0a14', fontSize: '10px', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          ⚡ Boosted <span style={{ fontWeight: 700, letterSpacing: 0, textTransform: 'none' }}>· {boostLeft(post)}</span>
        </div>
      )}
      {post.is_repost && (
        <div style={{ fontSize: '11px', color: '#666', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Repeat2 size={11} /> Reposted by {post.username} · Originally by {post.original_author}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
        <Avatar src={post.avatar} name={post.username} size={44} fontSize={18} />
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>{post.username}<UserBadgeGroup badges={post.user_badges} size="md" /><IdHunterBadge userId={post.user_id} /></div>
          <div style={{ fontSize: '11px', color: '#555' }}>{new Date(post.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
        </div>
        <span style={{ fontSize: '10px', padding: '3px 10px', borderRadius: '10px', background: `${contentColor}20`, color: contentColor, fontWeight: 600, border: `1px solid ${contentColor}40`, marginLeft: 'auto' }}>
          {getContentLabel(post.content_type)}
        </span>
      </div>

      {post.caption && <div style={{ fontSize: '15px', color: '#ddd', lineHeight: 1.6, marginBottom: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{post.caption}</div>}

      {post.planner_event_id && <AnnouncementClaimCTA eventId={post.planner_event_id} />}

      {/* Shared Track Card (Phase 2 share-to-Wall) */}
      {post.shared_track && (
        <SharedTrackCard track={post.shared_track} />
      )}
      {post.shared_event && (
        <SharedEventCard event={post.shared_event} />
      )}

      {post.media_url && (
        <div style={{ borderRadius: '10px', overflow: 'hidden', marginBottom: '14px' }}>
          {post.media_type === 'video' ? (
            <video src={`${BACKEND_URL}${post.media_url}`} controls autoPlay style={{ width: '100%', maxHeight: '400px' }} />
          ) : (
            <img src={`${BACKEND_URL}${post.media_url}`} alt="" style={{ width: '100%', borderRadius: '10px' }} />
          )}
        </div>
      )}

      {/* Reactions display */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {REACTIONS.map(r => {
          const count = post.reaction_counts?.[r.type] || 0;
          const userReacted = (post.reactions?.[r.type] || []).includes(currentUserId);
          return (
            <button
              key={r.type}
              data-testid={`expanded-reaction-${r.type}`}
              onClick={() => onReact(post.id, r.type)}
              style={{
                padding: '5px 10px', fontSize: '13px', borderRadius: '10px', cursor: 'pointer',
                border: userReacted ? '1px solid rgba(6,182,212,0.4)' : '1px solid rgba(255,255,255,0.08)',
                background: userReacted ? 'rgba(6,182,212,0.1)' : 'rgba(255,255,255,0.03)',
                color: '#ccc', display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.15s',
              }}
            >
              {r.emoji} {count > 0 && <span style={{ fontSize: '11px', fontWeight: 600 }}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '12px' }}>
        {post.user_id !== currentUserId && (() => {
          const alreadyReposted = (post.reposted_by || []).includes(currentUserId);
          return (
            <button onClick={() => onRepost(post)} data-testid={`repost-btn-expanded-${post.id}`} title={alreadyReposted ? 'Undo repost' : 'Repost to your Wall'} style={{ padding: '6px 12px', fontSize: '11px', borderRadius: '8px', cursor: 'pointer', border: alreadyReposted ? '1px solid rgba(34,211,238,0.5)' : '1px solid rgba(255,255,255,0.08)', background: alreadyReposted ? 'rgba(34,211,238,0.12)' : 'rgba(255,255,255,0.03)', color: alreadyReposted ? '#22d3ee' : '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Repeat2 size={11} /> {alreadyReposted ? 'Reposted' : 'Repost'} {post.repost_count > 0 && `(${post.repost_count})`}
            </button>
          );
        })()}
        <button onClick={() => onShare(post)} style={{ padding: '6px 12px', fontSize: '11px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Share2 size={11} /> Share
        </button>
        <BoostButton post={post} currentUserId={currentUserId} isAdmin={isAdmin} onBoosted={onBoosted} size="lg" />
        {post.user_id === currentUserId && (
          <button onClick={() => onEdit(post)} title="Edit caption or content type. To change media, delete and create a new post." style={{ padding: '6px 12px', fontSize: '11px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Edit2 size={11} /> Edit
          </button>
        )}
        {(post.user_id === currentUserId || isAdmin) && (
          <button onClick={() => { onDelete(post.id); onBack(); }} style={{ padding: '6px 12px', fontSize: '11px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(255,80,80,0.15)', background: 'rgba(255,80,80,0.05)', color: '#ff5555', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Trash2 size={11} /> Delete
          </button>
        )}
      </div>
    </div>
  );
};

// ============ CREATE POST FORM ============
const CreatePostForm = ({ currentUserId, currentUsername, currentAvatar, isAdmin, onClose, onPostCreated }) => {
  const [contentType, setContentType] = useState('general');
  const [caption, setCaption] = useState('');
  const [media, setMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  // Highlight state — toggle ON/OFF + color choice for regular users (admin auto-locked to cyan/magenta)
  const [highlightOn, setHighlightOn] = useState(false);
  const [highlightColor, setHighlightColor] = useState('yellow'); // default pick when toggled ON
  const fileRef = useRef(null);

  const handleMediaSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File too large. Max: ${isVideo ? '10MB' : '5MB'}`);
      return;
    }
    setMedia(file);
    setMediaPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!caption.trim() && !media) return;
    setPosting(true);
    try {
      const formData = new FormData();
      formData.append('content_type', contentType);
      formData.append('caption', caption);
      formData.append('user_id', currentUserId);
      formData.append('username', currentUsername);
      formData.append('avatar', currentAvatar || '');
      // Highlight: admin is always 'admin' (backend will also auto-stamp), regular users send chosen color or none
      let highlight = 'none';
      if (isAdmin && highlightOn) highlight = 'admin';
      else if (!isAdmin && highlightOn) highlight = highlightColor;
      formData.append('highlight', highlight);
      if (media) formData.append('media', media);

      const res = await fetch(`${BACKEND_URL}/api/wall/posts`, { method: 'POST', body: formData });
      if (!res.ok) {
        let detail = '';
        try { const j = await res.json(); detail = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail || j); } catch (_) {}
        throw new Error(`Failed to create post (${res.status})${detail ? ': ' + detail : ''}`);
      }
      const post = await res.json();
      onPostCreated(post);
      onClose();
    } catch (e) {
      alert(e.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>Create Post</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}><X size={16} /></button>
      </div>

      {/* Content type */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ fontSize: '10px', color: '#666', display: 'block', marginBottom: '4px' }}>Content Type</label>
        <div style={{ position: 'relative' }}>
          <select
            data-testid="post-content-type"
            value={contentType}
            onChange={e => setContentType(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', fontSize: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: '#0a0e1a', color: '#fff', cursor: 'pointer', appearance: 'none' }}
          >
            {CONTENT_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <ChevronDown size={12} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#555', pointerEvents: 'none' }} />
        </div>
      </div>

      {/* Caption */}
      <textarea
        data-testid="post-caption"
        value={caption}
        onChange={e => setCaption(e.target.value)}
        placeholder="What's on your mind?"
        style={{ width: '100%', padding: '10px 12px', fontSize: '13px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: '#0a0e1a', color: '#fff', resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' }}
      />

      {/* Media upload */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', marginBottom: '12px' }}>
        <button
          onClick={() => { fileRef.current.accept = 'image/*'; fileRef.current.click(); }}
          style={{ padding: '6px 12px', fontSize: '11px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(6,182,212,0.3)', background: 'rgba(6,182,212,0.08)', color: '#22d3ee', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <Image size={12} /> Photo
        </button>
        <button
          onClick={() => { fileRef.current.accept = 'video/*'; fileRef.current.click(); }}
          style={{ padding: '6px 12px', fontSize: '11px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(192,38,211,0.3)', background: 'rgba(192,38,211,0.08)', color: '#e879f9', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <Film size={12} /> Video (60s max)
        </button>
        <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleMediaSelect} />
      </div>

      {/* Media preview */}
      {mediaPreview && (
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          {media?.type?.startsWith('video/') ? (
            <video src={mediaPreview} style={{ width: '100%', maxHeight: '150px', borderRadius: '8px' }} controls />
          ) : (
            <img src={mediaPreview} alt="" style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }} />
          )}
          <button onClick={() => { setMedia(null); setMediaPreview(null); }} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', padding: '4px', cursor: 'pointer', color: '#fff' }}>
            <X size={12} />
          </button>
        </div>
      )}

      {/* Highlight Post Section */}
      <div data-testid="highlight-post-section" style={{ marginBottom: '14px', padding: '10px 12px', borderRadius: '10px', background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}>
            Highlight this post
            <span title="Please only highlight if necessary." style={{ display: 'inline-flex', alignItems: 'center', color: '#06b6d4', cursor: 'help' }}>
              <Info size={12} />
            </span>
          </label>
          {/* Toggle */}
          <button
            data-testid="highlight-toggle"
            onClick={() => setHighlightOn(v => !v)}
            style={{
              position: 'relative', width: '38px', height: '20px', borderRadius: '999px',
              background: highlightOn ? 'linear-gradient(135deg, #06b6d4, #c026d3)' : 'rgba(255,255,255,0.1)',
              border: 'none', cursor: 'pointer', transition: 'all 0.2s ease', padding: 0,
            }}
          >
            <span style={{ position: 'absolute', top: '2px', left: highlightOn ? '20px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s ease' }} />
          </button>
        </div>
        <div style={{ fontSize: '10px', color: '#888', marginTop: '4px', fontStyle: 'italic' }}>
          Please only highlight if necessary.
        </div>

        {/* Color picker — only when highlight is ON */}
        {highlightOn && (
          <div style={{ marginTop: '10px' }}>
            {isAdmin ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="wall-highlight-swatch admin selected" />
                <span style={{ fontSize: '11px', color: '#aaa' }}>BEATSEEK admin theme (auto-applied)</span>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '10px', color: '#666', marginBottom: '6px' }}>Pick a theme</div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {['yellow', 'red', 'purple'].map(color => (
                    <button
                      key={color}
                      data-testid={`highlight-color-${color}`}
                      onClick={() => setHighlightColor(color)}
                      className={`wall-highlight-swatch ${color} ${highlightColor === color ? 'selected' : ''}`}
                      style={{ outline: 'none' }}
                      title={`${color.charAt(0).toUpperCase()}${color.slice(1)} theme`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post button */}
      <button
        data-testid="submit-post"
        onClick={handleSubmit}
        disabled={posting || (!caption.trim() && !media)}
        style={{
          width: '100%', padding: '10px', fontSize: '13px', fontWeight: 700, borderRadius: '8px',
          border: 'none', cursor: posting ? 'wait' : 'pointer',
          background: 'linear-gradient(135deg, rgba(6,182,212,0.3), rgba(192,38,211,0.3))',
          color: '#fff', opacity: posting || (!caption.trim() && !media) ? 0.5 : 1,
        }}
      >
        {posting ? 'Posting...' : 'Post to Wall'}
      </button>
    </div>
  );
};

// ============ EDIT POST FORM ============
const EditPostForm = ({ post, onClose, onPostUpdated }) => {
  const [caption, setCaption] = useState(post.caption || '');
  const [contentType, setContentType] = useState(post.content_type);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/wall/posts/${post.id}?user_id=${post.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption, content_type: contentType }),
      });
      if (!res.ok) throw new Error('Failed to update');
      const updated = await res.json();
      onPostUpdated(updated);
      onClose();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>Edit Post</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}><X size={16} /></button>
      </div>
      <div style={{ fontSize: '10px', color: '#f59e0b', marginBottom: '12px', fontStyle: 'italic' }}>
        To change media, please delete and create a new post.
      </div>
      <div style={{ marginBottom: '12px' }}>
        <label style={{ fontSize: '10px', color: '#666', display: 'block', marginBottom: '4px' }}>Content Type</label>
        <select value={contentType} onChange={e => setContentType(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: '#0a0e1a', color: '#fff' }}>
          {CONTENT_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <textarea value={caption} onChange={e => setCaption(e.target.value)} style={{ width: '100%', padding: '10px', fontSize: '13px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: '#0a0e1a', color: '#fff', resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' }} />
      <button onClick={handleSave} disabled={saving} style={{ width: '100%', marginTop: '10px', padding: '10px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, rgba(6,182,212,0.3), rgba(192,38,211,0.3))', color: '#fff' }}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
};

const SidebarStyles = () => (
  <style>{`
    @keyframes beatseekPresenceBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
    .wall-sidebar-scroll::-webkit-scrollbar { width: 5px; }
    .wall-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
    .wall-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.35); border-radius: 999px; }
  `}</style>
);

// ============ FOLLOWER SIDEBAR ============
const MutualFacesCard = ({ user, rect }) => {
  const faces = user.mutual_users || [];
  const top = Math.min(Math.max(12, rect.top - 6), window.innerHeight - (70 + faces.length * 26));
  return createPortal(
    <div
      data-testid={`mutual-faces-${user.user_id}`}
      style={{
        position: 'fixed', left: `${rect.right + 10}px`, top: `${top}px`, zIndex: 100010,
        width: '196px', padding: '10px 12px', borderRadius: '12px',
        background: 'linear-gradient(180deg, #0d1424 0%, #07070b 100%)',
        border: '1px solid rgba(0,229,255,0.3)', boxShadow: '0 14px 34px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,229,255,0.08)',
        pointerEvents: 'none', animation: 'none',
      }}
    >
      <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#00e5ff', marginBottom: '8px' }}>
        Followed by {user.mutual_count > faces.length ? `${faces.length} of ${user.mutual_count}` : user.mutual_count} in your circle
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {faces.map(f => (
          <div key={f.user_id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {f.avatar
              ? <img src={f.avatar} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />
              : <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg, #06b6d4, #c026d3)', color: '#fff', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{(f.username || '?').charAt(0).toUpperCase()}</span>}
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.username}</span>
          </div>
        ))}
        {user.mutual_count > faces.length && (
          <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>+{user.mutual_count - faces.length} more</span>
        )}
      </div>
    </div>,
    document.body,
  );
};

const FollowerSidebar = ({ userId, activeFilter, onFilterUser, endpoint = 'following-users', emptyText = 'No connections yet', allTestId = 'filter-all-following', allLabel = 'All' }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [cardUser, setCardUser] = useState(null);

  useEffect(() => {
    if (!userId) return;
    fetch(`${BACKEND_URL}/api/wall/${endpoint}?user_id=${userId}&skip=${skip}&limit=25`)
      .then(r => r.json())
      .then(d => { setUsers(prev => skip === 0 ? (d.users || []) : [...prev, ...(d.users || [])]); setTotal(d.total || 0); })
      .catch(() => {});
  }, [userId, skip, endpoint]);

  if (!users || users.length === 0) {
    return (
      <div style={{ width: '80px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', borderRight: '1px solid rgba(255,255,255,0.06)', padding: '12px 8px' }}>
        <div style={{ fontSize: '10px', color: '#555', textAlign: 'center', padding: '10px 4px' }}>{emptyText}</div>
      </div>
    );
  }

  return (
    <div className="wall-sidebar-scroll" style={{ width: '84px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', borderRight: '1px solid rgba(255,255,255,0.06)', padding: '12px 6px 12px 2px', overflowY: 'auto', overflowX: 'hidden', direction: 'rtl' }}>
      {/* All / Suggested button */}
      <button
        dir="ltr"
        data-testid={allTestId}
        onClick={() => onFilterUser(null)}
        title={allLabel === 'All' ? 'Show everyone you follow' : 'Suggested from your follows, friends and mutuals'}
        style={{
          width: allLabel.length > 4 ? '70px' : '44px',
          height: allLabel.length > 4 ? '24px' : '44px',
          borderRadius: allLabel.length > 4 ? '999px' : '50%',
          cursor: 'pointer', fontSize: allLabel.length > 4 ? '9px' : '10px', fontWeight: 700, letterSpacing: '0.04em',
          border: !activeFilter ? '2px solid #06b6d4' : '2px solid rgba(255,255,255,0.1)',
          background: !activeFilter ? 'rgba(6,182,212,0.15)' : 'rgba(255,255,255,0.03)',
          color: !activeFilter ? '#22d3ee' : '#666',
          boxShadow: !activeFilter ? '0 0 10px rgba(6,182,212,0.3)' : 'none',
        }}
      >{allLabel}</button>

      {users.map(u => (
        <div key={u.user_id} dir="ltr" style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
          <button
            data-testid={`filter-user-${u.user_id}`}
            onClick={e => setCardUser({ user: u, rect: e.currentTarget.getBoundingClientRect() })}
            title={u.reason ? `${u.username} · ${u.reason}` : u.username}
            style={{
              width: '44px', height: '44px', borderRadius: '50%', cursor: 'pointer', overflow: 'hidden',
              border: activeFilter === u.user_id ? '2px solid #c026d3' : '2px solid rgba(255,255,255,0.08)',
              background: 'linear-gradient(135deg, #06b6d4, #c026d3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '16px', fontWeight: 700,
              boxShadow: activeFilter === u.user_id ? '0 0 14px 3px rgba(192,38,211,0.55)' : 'none',
              transition: 'transform 0.2s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s, border-color 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.45), 0 0 16px 4px rgba(6,182,212,0.6)';
              e.currentTarget.style.borderColor = 'rgba(34,211,238,0.85)';
              if ((u.mutual_users || []).length) setHovered({ user: u, rect: e.currentTarget.getBoundingClientRect() });
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = activeFilter === u.user_id ? '0 0 14px 3px rgba(192,38,211,0.55)' : 'none';
              e.currentTarget.style.borderColor = activeFilter === u.user_id ? '#c026d3' : 'rgba(255,255,255,0.08)';
              setHovered(null);
            }}
          >
            {u.avatar ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : u.username?.charAt(0)?.toUpperCase()}
          </button>
          <span
            data-testid={`presence-${u.user_id}`}
            title={u.online ? `${u.username} is online` : `${u.username} is offline`}
            style={{
              position: 'absolute', top: '30px', right: '10px', width: '11px', height: '11px', borderRadius: '50%',
              border: '2px solid #0b1120', pointerEvents: 'none',
              background: u.online ? '#22c55e' : '#6b7280',
              boxShadow: u.online ? '0 0 8px rgba(34,197,94,0.9)' : 'none',
              animation: u.online ? 'beatseekPresenceBlink 1.6s ease-in-out infinite' : 'none',
            }}
          />
          <span style={{ fontSize: '9px', color: '#ccc', fontWeight: 600, textAlign: 'center', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{u.username}</span>
        </div>
      ))}

      {users.length < total && (
        <button onClick={() => setSkip(s => s + 25)} style={{ fontSize: '9px', color: '#06b6d4', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
          Show more
        </button>
      )}

      <SidebarStyles />

      {cardUser && (
        <WallUserCard
          user={cardUser.user}
          rect={cardUser.rect}
          viewerId={userId}
          mutualUsers={cardUser.user.mutual_users || []}
          onShowPosts={() => onFilterUser(cardUser.user.user_id)}
          onClose={() => setCardUser(null)}
        />
      )}

      {hovered && !cardUser && <MutualFacesCard user={hovered.user} rect={hovered.rect} />}
    </div>
  );
};

// ============ MAIN WALL MODAL ============
const TheWall = ({ isOpen, onClose, currentUser }) => {
  const [feedType, setFeedType] = useState('all');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('feed'); // feed, create, edit, expanded
  const [expandedPost, setExpandedPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [filterUserId, setFilterUserId] = useState(null);
  const [needsId, setNeedsId] = useState(false);
  const [shareToast, setShareToast] = useState(null);
  const [slideDir, setSlideDir] = useState('none');
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [narrow, setNarrow] = useState(typeof window !== 'undefined' ? window.innerWidth < 1100 : false);
  const feedRef = useRef(null); // none, left, right

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 1100);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const userId = currentUser?._id || currentUser?.id || '';
  const username = currentUser?.username || currentUser?.name || '';
  const avatar = currentUser?.avatar || currentUser?.profile_picture || '';
  const isAdmin = currentUser?.role === 'admin';

  const loadPosts = useCallback(async (append = false) => {
    if (append) { setLoadingMore(true); } else { setLoading(true); }
    try {
      const skip = append ? posts.length : 0;
      const params = new URLSearchParams({ feed_type: feedType, limit: '20', skip: String(skip) });
      if (userId) params.set('user_id', userId);
      if (filterUserId) params.set('filter_user_id', filterUserId);
      if (needsId) params.set('needs_id', 'true');
      const res = await fetch(`${BACKEND_URL}/api/wall/posts?${params}`);
      const data = await res.json();
      const newPosts = data.posts || [];
      if (append) {
        setPosts(prev => [...prev, ...newPosts]);
      } else {
        setPosts(newPosts);
      }
      setHasMore(newPosts.length >= 20);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [feedType, userId, filterUserId, needsId, posts.length]);

  useEffect(() => {
    if (isOpen) { setPosts([]); setHasMore(true); }
  }, [isOpen, feedType, filterUserId, needsId]);

  useEffect(() => {
    if (isOpen && posts.length === 0 && hasMore) loadPosts(false);
  }, [isOpen, posts.length, hasMore, loadPosts]);

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
      loadPosts(true);
    }
  }, [hasMore, loadingMore, loading, loadPosts]);

  const handleReact = async (postId, reactionType) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/wall/posts/${postId}/reaction?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reaction_type: reactionType }),
      });
      const data = await res.json();
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, reactions: data.reactions, reaction_counts: data.reaction_counts } : p));
      if (expandedPost?.id === postId) setExpandedPost(prev => ({ ...prev, reactions: data.reactions, reaction_counts: data.reaction_counts }));
    } catch (e) { console.error(e); }
  };

  const handleRepost = async (post) => {
    const postId = typeof post === 'string' ? post : post.id;
    const alreadyReposted = typeof post === 'object' && (post.reposted_by || []).includes(userId);
    try {
      if (alreadyReposted) {
        // Undo an accidental repost
        const res = await fetch(`${BACKEND_URL}/api/wall/posts/${postId}/repost?user_id=${userId}`, { method: 'DELETE' });
        if (res.ok) loadPosts();
      } else {
        const res = await fetch(`${BACKEND_URL}/api/wall/posts/${postId}/repost?user_id=${userId}&username=${username}&avatar=${avatar}`, { method: 'POST' });
        if (res.ok) loadPosts();
      }
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/wall/posts/${postId}?user_id=${userId}&is_admin=${isAdmin}`, { method: 'DELETE' });
      loadPosts();
    } catch (e) { console.error(e); }
  };

  const handleBoosted = (updated) => {
    setPosts(prev => [updated, ...prev.filter(p => p.id !== updated.id)]);
    setExpandedPost(prev => (prev && prev.id === updated.id ? updated : prev));
  };

  const handleShare = (post) => {
    const text = `${post.username}: ${post.caption?.substring(0, 100) || ''}`;
    navigator.clipboard.writeText(text);
    setShareToast('Link copied!');
    setTimeout(() => setShareToast(null), 2000);
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="the-wall-overlay"
      style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
    >
      {/* Featured Emote of the Week sidebar (desktop only, hidden on feed-only modes) */}
      {view === 'feed' && (
        <div onClick={e => e.stopPropagation()}>
          <BuildListPanel />
          <IdeasVotePanel userId={currentUser?._id || currentUser?.id} />
          <NeedsIdRail active={needsId} onToggle={setNeedsId} />
          <IdLeaderboardRail />
          <EmoteOfTheWeek currentUser={currentUser} onPostShared={() => loadPosts(false)} />
        </div>
      )}
      <div
        data-testid="the-wall-modal"
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '700px', maxHeight: '90vh', borderRadius: '16px',
          position: 'relative',
          background: '#0b1120',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ background: '#0b1120', borderRadius: '14px', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: view === 'expanded' ? 0 : '10px' }}>
              <div>
                {needsId && view === 'feed' ? (
                  <button
                    type="button"
                    data-testid="wall-back-to-wall"
                    onClick={() => { setNeedsId(false); setFilterUserId(null); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#00e5ff', cursor: 'pointer', fontSize: '13px', fontWeight: 800, letterSpacing: '0.5px', padding: 0 }}
                  >
                    <ArrowLeft size={15} /> Back to the Wall
                  </button>
                ) : (
                  <div style={{ fontSize: '18px', fontWeight: 900, color: '#fff', letterSpacing: '2px' }}>THE WALL</div>
                )}
                {!(needsId && view === 'feed') && (
                <button
                  type="button"
                  data-testid="wall-back-to-dashboard"
                  onClick={onClose}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em', padding: '2px 0 0' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#22d3ee'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; }}
                >
                  <ArrowLeft size={11} /> Back to dashboard
                </button>
                )}
              </div>
              {view === 'expanded' ? (
                <button
                  data-testid="back-to-wall-header"
                  onClick={() => { setView('feed'); setExpandedPost(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#06b6d4', cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: 0 }}
                >
                  <ArrowLeft size={14} /> Back to Wall
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    data-testid="create-post-btn"
                    onClick={() => setView('create')}
                    style={{ padding: '6px 14px', fontSize: '11px', fontWeight: 700, borderRadius: '8px', cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg, rgba(6,182,212,0.25), rgba(192,38,211,0.25))', color: '#fff', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Plus size={12} /> Create Post
                  </button>
                  <button data-testid="wall-close-btn" onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}><X size={18} /></button>
                </div>
              )}
            </div>

            {/* Feed toggle - Pill slider — hidden when viewing an expanded post */}
            {view !== 'expanded' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: feedType === 'all' ? '#00ffff' : 'rgba(255,255,255,0.4)', transition: 'color 0.3s' }}>BeatSeekers</span>
              <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', cursor: 'pointer' }}>
                <input
                  data-testid="feed-toggle"
                  type="checkbox"
                  checked={feedType === 'following'}
                  onChange={(e) => {
                    const next = e.target.checked ? 'following' : 'all';
                    setSlideDir(next === 'following' ? 'left' : 'right');
                    setTimeout(() => { setFeedType(next); setFilterUserId(null); setSlideDir('none'); }, 300);
                  }}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                  background: feedType === 'following' ? '#c026d3' : '#00ffff',
                  borderRadius: '26px', transition: '0.3s',
                }}>
                  <span style={{
                    position: 'absolute', height: '20px', width: '20px',
                    left: feedType === 'following' ? '25px' : '3px', bottom: '3px',
                    background: '#fff', borderRadius: '50%', transition: '0.3s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }} />
                </span>
              </label>
              <span style={{ fontSize: '12px', fontWeight: 600, color: feedType === 'following' ? '#c026d3' : 'rgba(255,255,255,0.4)', transition: 'color 0.3s' }}>Following</span>
              {/* Mobile/tablet only — desktop uses the Needs ID pull-out tab on the right rail */}
              {narrow && (
              <button
                type="button"
                data-testid="wall-needs-id-filter"
                onClick={() => setNeedsId(v => !v)}
                title={needsId ? 'Showing only clips nobody has ID\'d yet — tap to show everything' : 'Show only video clips that still need a Track ID'}
                style={{
                  marginLeft: '6px', padding: '4px 10px', borderRadius: 999, cursor: 'pointer', fontSize: '11px', fontWeight: 800, letterSpacing: '0.03em',
                  border: needsId ? '1px solid #ff2d78' : '1px solid rgba(255,45,120,0.4)',
                  background: needsId ? 'rgba(255,45,120,0.22)' : 'rgba(255,45,120,0.06)', color: needsId ? '#fff' : '#ff7aa8',
                  boxShadow: needsId ? '0 0 12px rgba(255,45,120,0.45)' : 'none', transition: 'all 0.2s',
                }}
              >
                🎯 Needs ID{needsId ? ' ✓' : ''}
              </button>
              )}
            </div>
            )}
          </div>

          {/* Content */}
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Sidebar — followers on the Following tab, suggested BeatSeekers on the All tab */}
            {view === 'feed' && (
              feedType === 'following' ? (
                <FollowerSidebar key="following" userId={userId} activeFilter={filterUserId} onFilterUser={setFilterUserId} />
              ) : (
                <FollowerSidebar
                  key="suggested"
                  userId={userId}
                  activeFilter={filterUserId}
                  onFilterUser={setFilterUserId}
                  endpoint="suggested-users"
                  emptyText="No suggestions yet"
                  allTestId="filter-all-suggested"
                  allLabel="Suggested"
                />
              )
            )}

            {/* Main content */}
            <div ref={feedRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', padding: view === 'feed' ? '12px 14px' : '0', transition: 'transform 0.3s ease, opacity 0.3s ease', transform: slideDir === 'left' ? 'translateX(-100%)' : slideDir === 'right' ? 'translateX(100%)' : 'translateX(0)', opacity: slideDir !== 'none' ? 0 : 1 }}>
              {view === 'create' && (
                <CreatePostForm
                  currentUserId={userId}
                  currentUsername={username}
                  currentAvatar={avatar}
                  isAdmin={isAdmin}
                  onClose={() => setView('feed')}
                  onPostCreated={(post) => { setPosts(prev => [post, ...prev]); }}
                />
              )}

              {view === 'edit' && editingPost && (
                <EditPostForm
                  post={editingPost}
                  onClose={() => { setView('feed'); setEditingPost(null); }}
                  onPostUpdated={(updated) => { setPosts(prev => prev.map(p => p.id === updated.id ? updated : p)); }}
                />
              )}

              {view === 'expanded' && expandedPost && (
                <ExpandedPost
                  post={expandedPost}
                  currentUserId={userId}
                  currentUsername={username}
                  currentAvatar={avatar}
                  isAdmin={isAdmin}
                  onBack={() => { setView('feed'); setExpandedPost(null); }}
                  onReact={handleReact}
                  onRepost={handleRepost}
                  onEdit={(p) => { setEditingPost(p); setView('edit'); }}
                  onDelete={handleDelete}
                  onShare={handleShare}
                  onBoosted={handleBoosted}
                />
              )}

              {view === 'feed' && (
                <>
                  <WallSpotlightMobile currentUser={currentUser} />
                  {loading && (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#555', fontSize: '12px' }}>Loading...</div>
                  )}
                  {!loading && posts.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#555', fontSize: '12px' }} data-testid="wall-empty-state">
                      {needsId ? '🎯 Every clip on the Wall has been ID\'d — the hunters are on top of it. Check back soon.' : 'No posts yet. Be the first to post!'}
                    </div>
                  )}
                  {/* Leaderboard lives in the left pull-out rail on desktop; inline on mobile/tablet */}
                  {narrow && <WallIdLeaderboard />}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {posts.map(post => (
                      <PostCard
                        key={post.id}
                        post={post}
                        currentUserId={userId}
                        currentUsername={username}
                        currentAvatar={avatar}
                        isAdmin={isAdmin}
                        onReact={handleReact}
                        onRepost={handleRepost}
                        onEdit={(p) => { setEditingPost(p); setView('edit'); }}
                        onDelete={handleDelete}
                        onExpand={(p) => { setExpandedPost(p); setView('expanded'); }}
                        onShare={handleShare}
                        onBoosted={handleBoosted}
                      />
                    ))}
                    {loadingMore && (
                      <div style={{ textAlign: 'center', padding: '16px', color: '#555', fontSize: '11px' }}>Loading more...</div>
                    )}
                    {!hasMore && posts.length > 0 && (
                      <div style={{ textAlign: 'center', padding: '16px', color: '#333', fontSize: '10px' }}>You've reached the end</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Share toast */}
          {shareToast && (
            <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', padding: '8px 20px', borderRadius: '8px', background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', color: '#00ff88', fontSize: '12px', fontWeight: 600 }}>
              {shareToast}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TheWall;
